<template>
  <div class="basic-listing">
    <template v-if="$isDesktop">
      <AsyncCampsiteDetailHeroDesktop
        :campsite="campsite"
        :is-dated="isDated"
        :has-availability="false"
        :types="types"
        :primary-photo="campsite.primaryPhoto"
      />
      <AsyncCampsiteDescriptionDesktop :campsite="campsite" />
    </template>
    <template v-else>
      <section
        class="cs-photos"
        :class="{ 'no-primary': !campsite.primaryPhoto }"
      >
        <client-only>
          <campsite-favourite-button
            :campsite-id="campsite.id"
            :campsite-name="campsite.name"
            class="cs-favourite"
          />
        </client-only>

        <AsyncCampsitePhotosProvider
          :key="getAsyncComponentKey('campsitePhotosProvider')"
          :search-result-index="0"
          :campsite-id="campsite.id"
          :campsite-name="campsite.name"
          :category-ids="[]"
          :gallery-full-screen="false"
          :primary-photo="campsite.primaryPhoto"
          :show-search-gallery="false"
          :photos-prop="seoPhotos"
          :photo-count="undefined"
          :load-photos-on-mount="true"
          :gallery-initial-index="galleryInitialIndex"
          @click="navigateToAlbums"
          @vue:mounted="setAsyncComponentKey('campsitePhotosProvider')"
        />
      </section>

      <div class="cs-card-content">
        <div data-test="campsite-rating-bubble-and-name">
          <AsyncRatingBubble
            v-if="campsite.rating"
            v-scrollto="{
              target: '.campsite-subsection.reviews',
              options: { duration: 200, offset: 0 },
            }"
            :rating="campsite.rating * 2"
            :rate-count="campsite.rateCount"
            class="campsite-rating"
          />
          <h1 class="cs-name">
            {{ campsite.name }}
          </h1>
        </div>

        <AsyncCampsiteDetailSearchBreadcrumbs
          v-bind="locationBreadcrumbsProps"
        />

        <div class="location-row">
          <AsyncCampsiteMapModal
            :key="getAsyncComponentKey('campsiteMapModal')"
            :campsite="campsite"
            @vue:mounted="setAsyncComponentKey('campsiteMapModal')"
          >
            <template #card>
              <lazy-campsite-map-card :campsite="mapCampsite">
                <template #footer>
                  <lazy-campsite-map-card-button
                    :is-bookable="false"
                    :hierarchy-path="campsite.path"
                    :slug="campsite.slug"
                  />
                </template>
              </lazy-campsite-map-card>
            </template>
          </AsyncCampsiteMapModal>
        </div>

        <AsyncNutshells
          v-if="campsite.nutshells && campsite.nutshells.length > 0"
          :key="getAsyncComponentKey('nutshells')"
          :nutshells="campsite.nutshells"
          :with-jump-link="true"
          @vue:mounted="setAsyncComponentKey('nutshells')"
        />
      </div>
      <AsyncCampsiteDetailFreeListingPromo />
    </template>

    <AsyncNearbyBookable
      :key="getAsyncComponentKey('nearbyBookable')"
      data-test="nearby-bookable"
      v-bind="nearbyBookableProps"
      @vue:mounted="setAsyncComponentKey('nearbyBookable')"
    />

    <AsyncCampsiteDetailsPromoPages
      :key="getAsyncComponentKey('campsiteDetailsPromoPages')"
      :slug="campsite.slug"
      class="campsite-subsection"
      @vue:mounted="setAsyncComponentKey('campsiteDetailsPromoPages')"
    />
    <AsyncSubsectionsGroup
      :key="getAsyncComponentKey('subsectionsGroup')"
      :campsite="campsite"
      @vue:mounted="setAsyncComponentKey('subsectionsGroup')"
    />

    <AsyncCampsiteReviewsSubsection
      :key="getAsyncComponentKey('campsiteReviewsSubsection')"
      :campsite-name="campsite.name"
      :campsite-slug="campsite.slug"
      :campsite-rate-count="campsite.rateCount"
      :campsite-rating="parseFloat(campsite.rating10.toFixed(1))"
      @vue:mounted="setAsyncComponentKey('campsiteReviewsSubsection')"
    />
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, hydrateOnIdle } from 'vue'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { campsiteToMapCampsite } from '../../campsites/maps'
import { MapCampsite } from '../../maps/domain/MapsCampsite'
import type { CampsiteDetails } from '../types'

const AsyncCampsiteDetailHeroDesktop = defineAsyncComponent({
  loader: () => import('./CampsiteDetailHeroDesktop.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsiteDescriptionDesktop = defineAsyncComponent({
  loader: () => import('./CampsiteDescriptionDesktop.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsitePhotosProvider = defineAsyncComponent({
  loader: () =>
    import(
      '../../../components/campsite/campsite-card/PuCampsitePhotosProvider.vue'
    ),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncRatingBubble = defineAsyncComponent({
  loader: () =>
    import(
      '../../../components/campsite/campsite-card/components/PuRatingBubble.vue'
    ),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsiteDetailSearchBreadcrumbs = defineAsyncComponent({
  loader: () => import('./CampsiteDetailSearchBreadcrumbs.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncCampsiteMapModal = defineAsyncComponent({
  loader: () => import('../../campsites/components/CampsiteMapModal.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncNutshells = defineAsyncComponent({
  loader: () =>
    import(
      '../../../components/campsite/campsite-card/components/PuNutshells.vue'
    ),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteDetailFreeListingPromo = defineAsyncComponent({
  loader: () => import('./CampsiteDetailFreeListingPromo.vue'),
  hydrate: hydrateOnIdle(),
})

const AsyncNearbyBookable = defineAsyncComponent({
  loader: () => import('../nearby/components/NearbyBookable.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteDetailsPromoPages = defineAsyncComponent({
  loader: () => import('../promo-pages/CampsiteDetailsPromoPages.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncSubsectionsGroup = defineAsyncComponent({
  loader: () => import('../../campsites/components/SubsectionsGroup.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncCampsiteReviewsSubsection = defineAsyncComponent({
  loader: () =>
    import(
      '../../campsites/components/subsections/CampsiteReviewsSubsection.vue'
    ),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  components: {
    AsyncSubsectionsGroup,
    AsyncCampsiteDetailHeroDesktop,
    AsyncCampsiteDescriptionDesktop,
    AsyncCampsitePhotosProvider,
    AsyncRatingBubble,
    AsyncCampsiteDetailSearchBreadcrumbs,
    AsyncCampsiteMapModal,
    AsyncNutshells,
    AsyncCampsiteDetailFreeListingPromo,
    AsyncNearbyBookable,
    AsyncCampsiteDetailsPromoPages,
    AsyncCampsiteReviewsSubsection,
  },
})
class CampsiteDetailFreeListing extends Vue {
  @Prop({ required: true })
  campsite: CampsiteDetails

  @Prop({ required: true })
  isDated: boolean

  @Prop({ required: true })
  types: number[]

  isMounted = false
  galleryInitialIndex = 0
  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  mounted() {
    if (this.$.isUnmounted) return

    const cssgal = document.getElementById('search-gallery-0')
    if (cssgal && cssgal.dataset.galleryPosition) {
      const leftPos = parseInt(cssgal.dataset.galleryPosition)

      if (cssgal && leftPos) {
        const galleryImages = cssgal.children
        for (let i = 0; i < galleryImages.length; i++) {
          if (
            leftPos + 50 > (galleryImages[i] as HTMLImageElement).offsetLeft &&
            leftPos - 50 < (galleryImages[i] as HTMLImageElement).offsetLeft
          ) {
            this.galleryInitialIndex = i
            break
          }
        }
      }
    }
    this.isMounted = true
  }

  get seoPhotos() {
    return this.campsite.seoPhotos
      ?.map((photo) => {
        const url = { masterImage: photo.url }
        return {
          ...photo,
          url,
        }
      })
      .slice(0, 10)
  }

  get locationBreadcrumbsProps() {
    return {
      hierarchyBreadcrumbs: this.campsite.hierarchyBreadcrumbs,
      countryIso: this.campsite.countryIso,
    }
  }

  get mapCampsite(): MapCampsite {
    return campsiteToMapCampsite(this.campsite)
  }

  get nearbyBookableProps() {
    return {
      campsiteSlug: this.campsite.slug,
      hierarchyPath: this.campsite.path,
      point: this.campsite.point,
      name: this.campsite.name,
      adminAreaHierarchy: this.campsite.hierarchyBreadcrumbs[1],
      countryHierarchy: this.campsite.hierarchyBreadcrumbs[0],
      basicListing: true,
    }
  }

  navigateToAlbums() {
    void this.$router.push({
      name: 'campsite-albums-album',
      params: this.$route.params,
      query: this.$route.query,
    })
  }
}

export default toNative(CampsiteDetailFreeListing)
</script>
<style scoped>
.cs-photos {
  height: 75vw;
  overflow: hidden;
}

.cs-photos.no-primary {
  height: auto;
  overflow: visible;
}

.no-primary .cs-favourite {
  top: 0;
}

.location-row {
  float: right;
  margin-top: 0.65em;
}
</style>
